import type { RewardRequest } from '../types/reward'

/**
 * Estados de exibição derivados do status de um resgate.
 * Apenas `redeemed`, `awaiting_pickup` e `pending_approval` são apresentados ao
 * aluno; qualquer outro caso (falha/cancelado) colapsa em `none`.
 *
 * Mapeamento a partir do status do backend:
 * - `delivered` → `redeemed`         (Resgatado)
 * - `approved`  → `awaiting_pickup`  (Aguardando retirada)
 * - `draft`     → `pending_approval` (Aguardando aprovação)
 *
 * **Não há prazo de retirada.** Um resgate aprovado fica aguardando retirada
 * indefinidamente. A checagem anterior escondia do aluno todo resgate `approved`
 * cujo `expires_at` já tivesse passado, contando que o backend fosse reconciliar
 * o status — o que nunca acontecia, e deixava o resgate em limbo: invisível para
 * o aluno, sem estorno das moedas, e pendente para o gestor no Admin.
 */
export type RewardRequestDisplayState =
  | 'redeemed'
  | 'awaiting_pickup'
  | 'pending_approval'
  | 'none'

export type RewardRequestVisibleState = Exclude<RewardRequestDisplayState, 'none'>

export const rewardRequestStateLabel: Record<RewardRequestVisibleState, string> = {
  redeemed: 'Resgatado',
  awaiting_pickup: 'Aguardando retirada',
  pending_approval: 'Aguardando aprovação'
}

export const resolveRewardRequestState = (
  request: RewardRequest
): RewardRequestDisplayState => {
  if (request.status === 'delivered') return 'redeemed'
  if (request.status === 'approved') return 'awaiting_pickup'
  if (request.status === 'draft') return 'pending_approval'
  return 'none'
}

/** Resgates que aparecem para o aluno (histórico, indicadores, modal). */
export const isDisplayableRewardRequest = (
  request: RewardRequest
): boolean => resolveRewardRequestState(request) !== 'none'

/** Há um resgate em processamento (aguardando aprovação) para este conjunto. */
export const hasPendingRewardRequest = (
  requests: RewardRequest[]
): boolean => requests.some(request => request.status === 'draft')
